'use client';

import { ReactElement, useEffect, useState } from 'react'
import { User } from '@payload-types';
import ButtonLink from '@components/text/button-link'
import API_Client from '@services/api-client'
import { DAYS_IN_SECONDS } from '@utils/constants'
import { expireAt, expired } from '@utils/dates'

interface PushNotificationsProps {
  user: User;
}

function isNotificationSupported(): boolean {
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasShowNotification = ServiceWorkerRegistration && 'showNotification' in ServiceWorkerRegistration.prototype;

  return hasServiceWorker && hasPushManager && hasShowNotification;
}

export default function PushNotifications({ user }: PushNotificationsProps): ReactElement {
  const [loading, setLoading] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [isDenied, setIsDenied] = useState<boolean>(false);
  const [isSubscripted, setIsSubscripted] = useState<boolean>(false);
  const [isIgnored, setIsIgnored] = useState<boolean>(false);

  const createSubscription = async () => {
    if (loading) {
      return;
    }

    setLoading(true);

    // Check was ignored
    const wasIgnored = window?.localStorage?.getItem('push_notification_ignored') === '1';
    if (wasIgnored) {
      setIsIgnored(true);
      setLoaded(true);
      setLoading(false);
    }

    // Check support
    if (! isNotificationSupported()) {
      setLoaded(true);
      setLoading(false);
      return;
    }

    let state: NotificationPermission = Notification.permission;
    if (state !== 'granted') {
      state = await Notification.requestPermission();
    }

    if (state !== 'granted') {
      if (state === 'denied') {
        setIsDenied(true);
      }

      setLoaded(true);
      setLoading(false);
      return null;
    }

    try {
      const args = {
        userVisibleOnly: true,
        applicationServerKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
      };

      await navigator.serviceWorker.register('/sw-push-notifications.js').then((registration) => {
        registration.onupdatefound = () => {
          const newWorker = registration.installing;
          if (! newWorker) {
            return;
          }

          newWorker.onstatechange = () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ action: 'skipWaiting' });
            }
          };
        };
      });

      const registration: ServiceWorkerRegistration = await navigator.serviceWorker.ready;

      let subscription: PushSubscription | null = await registration.pushManager.getSubscription();
      if (! subscription) {
        subscription = await registration.pushManager.subscribe(args);
      }

      const cached_date = window?.localStorage?.getItem('push_notification_expires_at');
      const cached_user = window?.localStorage?.getItem('push_notification_user_id');
      const cached_endpoint = window?.localStorage?.getItem('push_notification_endpoint');

      setIsSubscripted(true);

      /**
       * Will send to server when:
       * - User or endpoint is changed.
       * - Once a day.
       */
      if (subscription && (expired(cached_date) || cached_user !== String(user.id) || cached_endpoint !== subscription.endpoint)) {
        const response = await API_Client().post('push-notifications/subscription', { subscription });

        if (response?.success) {
          window?.localStorage?.setItem('push_notification_user_id', String(user.id));
          window?.localStorage?.setItem('push_notification_endpoint', subscription.endpoint);
          window?.localStorage?.setItem('push_notification_expires_at', expireAt(1 * DAYS_IN_SECONDS));
        }

        setIsSubscripted(response.success);
      }
    } catch (e) {
      console.error('Failed to register service-worker: ', e);
    }

    setLoading(false);
    setLoaded(true);
  }

  const ignoreSubscription = () => {
    setIsIgnored(true);
  }

  useEffect(() => {
    createSubscription();
  }, []);

  useEffect(() => {
    if (window && window.localStorage) {
      window.localStorage.setItem('push_notification_ignored', isIgnored ? '1' : '0');
    }
  }, [isIgnored]);

  return (
    <div>
      { ! isIgnored && ! loading && loaded && ! isDenied && ! isSubscripted && (
        <div className="push-notifications-wrapper push-notifications-default">
          <p>Aceite para receber notificações da Intranet no seu navegador.</p>
          <ButtonLink action={ () => createSubscription() }>Aceitar</ButtonLink>
          <ButtonLink action={ () => ignoreSubscription() } type="link-primary">Fechar</ButtonLink>
        </div>
      ) }
    </div>
  );
}
